import { createHttpClient } from "./http";

/**
 * Mensagem de erro vinda da API.
 *
 * O ValidationPipe do backend responde com um array de mensagens; sem juntar,
 * a tela mostraria "[object Object]" no lugar do motivo.
 */
function messageFrom(error) {
	const message = error.response?.data?.message;
	if (Array.isArray(message)) return message.join(" ");
	return message || null;
}

function withStatus(message, error) {
	const wrapped = new Error(message);
	wrapped.status = error.response?.status;
	return wrapped;
}

export class AuthRepository {
	constructor() {
		this.axios = createHttpClient({ timeout: 300000 });
	}

	async authenticate(username, password) {
		try {
			const result = await this.axios.post("auth/login-distribuidor", {
				username: username,
				password: password
			});

			return result.data.access_token;
		} catch (error) {
			if(error.response?.status === 404 || error.response?.status === 401)
				throw new Error("Usuário ou senha incorreto.");
			// Menor de 18 anos: o backend explica o motivo.
			if(error.response?.status === 403)
				throw new Error(messageFrom(error) ?? "Acesso não permitido.");
			if(error.response?.status === 500)
				throw new Error(error.response.data.message ?? "Erro interno.");

			throw new Error("Erro interno.");
		}
	}

	async getProfile() {
		try {
			const result = await this.axios.get("auth/profile-distribuidor", {
				headers: {
					Authorization: `Bearer ${localStorage.getItem("access_token")}`
				}
			});

			return result.data;
		} catch (error) {
			if(error.response?.status === 401)
				throw withStatus("Usuário não autenticado.", error);

			throw withStatus("Erro interno.", error);
		}
	}

	/** Dados da própria conta (tela de Configurações): nome, email e login. */
	async getAccount() {
		try {
			const result = await this.axios.get("auth/conta-distribuidor", {
				headers: {
					Authorization: `Bearer ${localStorage.getItem("access_token")}`
				}
			});

			return result.data;
		} catch (error) {
			if (error.response?.status === 401)
				throw withStatus("Usuário não autenticado.", error);

			throw withStatus("Erro ao carregar os dados da conta.", error);
		}
	}

	/**
	 * Atualiza nome, email e login da própria conta.
	 *
	 * A rota age sobre o usuário do token, então nenhum id vai no corpo — e o
	 * backend só grava estes três campos.
	 */
	async updateAccount({ nome, email, login }) {
		try {
			const result = await this.axios.patch(
				"auth/conta-distribuidor",
				{ nome, email, login },
				{
					headers: {
						Authorization: `Bearer ${localStorage.getItem("access_token")}`,
						"Content-Type": "application/json",
					},
				}
			);

			return result.data;
		} catch (error) {
			if (error.response?.status === 409)
				throw new Error(messageFrom(error) ?? "Este login já está em uso.");

			throw new Error(messageFrom(error) ?? "Erro ao salvar os dados da conta.");
		}
	}

	/**
	 * Troca a senha de quem já está logado.
	 *
	 * O usuário alvo é sempre o dono do token — nenhum id é enviado pelo
	 * cliente, para não abrir caminho de trocar a senha de terceiros.
	 *
	 * Espera-se do backend: 400 (ou 401) quando a senha atual não confere.
	 * O 401 desta rota é ignorado pelo interceptor de sessão (ver http.js),
	 * senão errar a senha atual derrubaria a sessão e jogaria no login.
	 */
	async changePassword(senhaAtual, novaSenha) {
		try {
			const result = await this.axios.patch(
				"auth/change-password-distribuidor",
				{ senhaAtual, novaSenha },
				{
					headers: {
						Authorization: `Bearer ${localStorage.getItem("access_token")}`,
						"Content-Type": "application/json",
					},
				}
			);

			return result.data;
		} catch (error) {
			const status = error.response?.status;

			if (status === 400 || status === 401 || status === 403)
				throw new Error(messageFrom(error) ?? "Senha atual incorreta.");

			throw new Error(messageFrom(error) ?? "Erro ao alterar a senha.");
		}
	}

	/**
	 * Data de nascimento informada no modal pós-login (usuário antigo, sem a
	 * data). 403 = menor de 18 anos: a tela avisa e encerra a sessão.
	 */
	async informarDataNascimento(dataNascimento) {
		try {
			const result = await this.axios.patch(
				"auth/data-nascimento-distribuidor",
				{ dataNascimento },
				{
					headers: {
						Authorization: `Bearer ${localStorage.getItem("access_token")}`,
						"Content-Type": "application/json",
					},
				}
			);

			return result.data;
		} catch (error) {
			throw withStatus(messageFrom(error) ?? "Erro ao salvar a data de nascimento.", error);
		}
	}

	async sendResetPasswordToken(username) {
		try {
			const result = await this.axios.post("auth/reset-password-distribuidor", {
				username: username
			});

			return result.data.userId;
		} catch (error) {
			if(error.response?.status === 404)
				throw new Error("Usuário não encontrado.");

			throw new Error("Erro interno.");
		}
	}

	async resetPasswordWithToken(body) {
		try {
			const result = await this.axios.post("auth/new-password-distribuidor", body);

			return result.data.userId;
		} catch (error) {
			if(error.response?.status === 404)
				throw new Error("Token inválido ou usuário inexistente.");
			if(error.response?.status === 406)
				throw new Error("Token inválido ou expirado.");

			throw new Error("Erro interno.");
		}
	}
}
